import ky from "ky";
import { validarDesafioLoginUnificado, validarSessaoLoginUnificado } from "./utils/authenticatorUtils";
import {
	aplicarTokenRotacionadoSpaceApi,
	atualizarTokenUmaVezSpaceApi,
	getTokenJwtSpaceApi,
	notificarFalhaAutenticacaoSpaceApi,
} from "../sessaoAutenticacao.space";
import { anexarErroApi, lerCorpoErroApi, obterErroApi } from "@/utils/errors/apiError";
import { DEFAULT_GROUP_ID } from "@/configs/runtime";
import { DEFAULT_HEADER_GROUP_ID, DEFAULT_SPACE_USERNAME } from "@/configs/system";
import type { CredenciaisEntrarJwt } from "@auth/services/jwt/tiposAutenticacaoJwt";
import { adicionarParametroConsulta, getStatusError, isSignedInteger } from "@/utils/utils";
import { obterBaseUrlSpace, spaceApiAuthenticator } from "../clienteSpace";
import type {
	AuthLoginDesafioOtpResponseDto,
	AuthLoginOtpRequestDto,
	AuthLoginRequestDto,
	AuthLoginResponseDto,
	GerarAutorizacaoRequest,
	GerarAutorizacaoResponse,
	IArvoreRestricoesResponse,
	IClonarGrupoUsuarioRequest,
	ICriarGrupoUsuarioRequest,
	ICrudGrupoUsuarioResponse,
	IEditarGrupoUsuarioRequest,
	IExcluirGrupoUsuarioRequest,
	IListarAuditoriaGrupoRequest,
	IListarAuditoriaGrupoResponse,
	IListarAuditoriaRecursoRequest,
	IListarAuditoriaRecursoResponse,
	IListarGruposUsuarioRequest,
	IListarGruposUsuarioResponse,
	IListarUsuariosGrupoRequest,
	IListarUsuariosGrupoResponse,
	ISalvarRestricoesGrupoRequest,
	ISpaceAuthLookupResponse,
	ISpaceLoginResponse,
	ISpaceMenuResponse,
	ISpaceVerificaPermissaoRequest,
	ISpaceVerificaRecursoRequest,
	IVerificaRecursosDaTelaResponse,
} from "../dtos/authenticator.dto";

// ------------------------------- CONSTANTS -------------------------------
const STATUS_PERMITE_LOGIN_POR_ID = new Set([400, 401, 404, 422]);
const HEADERS_GRUPO_PADRAO = { [DEFAULT_HEADER_GROUP_ID]: DEFAULT_GROUP_ID } as const;

// --------------------------------- TYPES ---------------------------------
export type ResultadoAuthLogin =
	| { readonly status: 200; readonly dados: AuthLoginResponseDto }
	| { readonly status: 202; readonly dados: AuthLoginDesafioOtpResponseDto };

type ExecutoresLoginSpace = {
	readonly porNome: (payload: CredenciaisEntrarJwt) => Promise<ISpaceLoginResponse>;
	readonly porId: (payload: CredenciaisEntrarJwt, id: number) => Promise<ISpaceLoginResponse>;
};

// -------------------------------- HELPERS --------------------------------
function normalizarGrupo(payloadGroupId?: string | null): string {
	return payloadGroupId || DEFAULT_GROUP_ID;
}

function normalizarNomeUsuario(nome: string | null | undefined): string {
	return String(nome ?? "")
		.trim()
		.toUpperCase();
}

function isSpaceUsername(nome: string | null | undefined): boolean {
	const nomeEsperado = normalizarNomeUsuario(DEFAULT_SPACE_USERNAME);
	const nomeAtual = normalizarNomeUsuario(nome);

	return nomeAtual.length > 0 && nomeEsperado.length > 0 && nomeAtual === nomeEsperado;
}

function marcarUsuarioSpace(item: ISpaceAuthLookupResponse): ISpaceAuthLookupResponse {
	if (!isSpaceUsername(item?.nome)) return item;

	return {
		...item,
		eh_usuario_space: true,
	};
}

function normalizarRespostaLookup(
	resposta: ISpaceAuthLookupResponse | ISpaceAuthLookupResponse[],
): ISpaceAuthLookupResponse[] {
	const itens = Array.isArray(resposta) ? resposta : [resposta];
	return itens.map(marcarUsuarioSpace);
}

function podeTentarLoginPorId(status: number | undefined, username: string): boolean {
	return status !== undefined && STATUS_PERMITE_LOGIN_POR_ID.has(status) && isSignedInteger(username);
}

function obterStatusErroLogin(error: unknown): number | undefined {
	const statusHttp = getStatusError(error);
	if (statusHttp !== undefined) return statusHttp;
	if (error instanceof Error && error.cause instanceof Response) return error.cause.status;
	return undefined;
}

async function enviarLogin(
	caminho: "auth/login" | "auth/login/otp",
	payload: AuthLoginRequestDto | AuthLoginOtpRequestDto,
	signal: AbortSignal,
): Promise<Response> {
	return ky.post(caminho, {
		prefixUrl: obterBaseUrlSpace("authenticator"),
		credentials: "include",
		json: payload,
		signal,
		retry: 0,
		timeout: false,
		hooks: {
			beforeError: [
				async (error) => {
					const corpo = await lerCorpoErroApi(error.response);
					if (corpo) anexarErroApi(error, corpo);
					return error;
				},
			],
		},
	});
}

async function lerSessao(resposta: Response): Promise<AuthLoginResponseDto> {
	const dados: AuthLoginResponseDto = await resposta.json();
	validarSessaoLoginUnificado(dados);
	return dados;
}

// ------------------------------- FUNCTIONS -------------------------------
export async function autenticarLoginUnificadoSpace(
	payload: AuthLoginRequestDto,
	signal: AbortSignal,
): Promise<ResultadoAuthLogin> {
	const resposta = await enviarLogin("auth/login", payload, signal);
	if (resposta.status === 200) return { status: 200, dados: await lerSessao(resposta) };
	if (resposta.status !== 202) throw new Error("Resposta inesperada ao autenticar.");
	const dados: AuthLoginDesafioOtpResponseDto = await resposta.json();
	validarDesafioLoginUnificado(dados);
	return { status: 202, dados };
}

export async function verificarOtpLoginUnificadoSpace(
	payload: AuthLoginOtpRequestDto,
	signal: AbortSignal,
): Promise<AuthLoginResponseDto> {
	const resposta = await enviarLogin("auth/login/otp", payload, signal);
	if (resposta.status !== 200) throw new Error("Resposta inesperada ao verificar o código.");
	return lerSessao(resposta);
}

// --------------------------------------------- LOGIN -------------------------------------------------------------
async function loginByNameSpace(payload: CredenciaisEntrarJwt): Promise<ISpaceLoginResponse> {
	return spaceApiAuthenticator
		.post("login", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(payload.groupId) },
			json: {
				nome: payload.username.trim(),
				senha: payload.password.trim(),
			},
		})
		.json<ISpaceLoginResponse>();
}

async function loginByIdSpace(payload: CredenciaisEntrarJwt, id: number): Promise<ISpaceLoginResponse> {
	return spaceApiAuthenticator
		.post("login", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(payload.groupId) },
			json: {
				id_cad_pessoa: id,
				senha: payload.password.trim(),
			},
		})
		.json<ISpaceLoginResponse>();
}

async function autenticarComExecutoresSpace(
	payload: CredenciaisEntrarJwt,
	executores: Readonly<ExecutoresLoginSpace>,
): Promise<ISpaceLoginResponse> {
	if (payload.id_cad_pessoa != null && isSignedInteger(String(payload.id_cad_pessoa)))
		return executores.porId(payload, payload.id_cad_pessoa);

	try {
		return await executores.porNome(payload);
	} catch (error) {
		const status = obterStatusErroLogin(error);

		if (podeTentarLoginPorId(status, payload.username)) {
			const id = Number(payload.username);
			if (Number.isFinite(id)) return executores.porId(payload, id);
		}

		throw error;
	}
}

const EXECUTORES_LOGIN_SESSAO: ExecutoresLoginSpace = {
	porNome: loginByNameSpace,
	porId: loginByIdSpace,
};

// ------------------------------------------- AUTENTICAÇÃO --------------------------------------------------------
export async function autenticarSpace(payload: CredenciaisEntrarJwt): Promise<ISpaceLoginResponse> {
	return autenticarComExecutoresSpace(payload, EXECUTORES_LOGIN_SESSAO);
}

// ------------------------------- HELPERS -------------------------------
async function solicitarGeracaoAutorizacao(payload: GerarAutorizacaoRequest): Promise<GerarAutorizacaoResponse> {
	return spaceApiAuthenticator
		.post("autorizacao/gerar", {
			json: payload,
			credentials: "omit",
			// A recuperação abaixo distingue o JWT inválido das credenciais do aprovador.
			hooks: { afterResponse: undefined },
		})
		.json<GerarAutorizacaoResponse>();
}

async function recuperarTokenSolicitante(tokenAnterior: string): Promise<string | null> {
	const tokenAtual = getTokenJwtSpaceApi();
	if (tokenAtual && tokenAtual !== tokenAnterior) return tokenAtual;
	try {
		return await atualizarTokenUmaVezSpaceApi();
	} catch {
		return null;
	}
}

export async function gerarAutorizacao(payload: GerarAutorizacaoRequest): Promise<GerarAutorizacaoResponse> {
	const tokenAnterior = getTokenJwtSpaceApi();
	if (!tokenAnterior) throw new Error("Sua sessão não está disponível. Entre novamente para solicitar autorização.");

	try {
		return await solicitarGeracaoAutorizacao(payload);
	} catch (error) {
		if (obterErroApi(error)?.code !== "TOKEN_INVALIDO") throw error;
	}

	const novoToken = await recuperarTokenSolicitante(tokenAnterior);
	if (!novoToken) {
		notificarFalhaAutenticacaoSpaceApi();
		throw new Error("Sua sessão expirou. Entre novamente para solicitar autorização.");
	}

	aplicarTokenRotacionadoSpaceApi(novoToken);

	try {
		return await solicitarGeracaoAutorizacao(payload);
	} catch (error) {
		if (obterErroApi(error)?.code === "TOKEN_INVALIDO") {
			notificarFalhaAutenticacaoSpaceApi();
			throw new Error("Sua sessão expirou. Entre novamente para solicitar autorização.");
		}
		throw error;
	}
}

export async function atualizarTokenAuthSpace(groupId: string): Promise<ISpaceLoginResponse> {
	return spaceApiAuthenticator
		.post("refresh", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(groupId) },
		})
		.json<ISpaceLoginResponse>();
}

// --------------------------------------------- LOOKUP USUARIO -----------------------------------------------------
export async function recuperarLoginPorNome(nome: string, groupId?: string, signal?: AbortSignal) {
	const resposta = await spaceApiAuthenticator
		.get("recupera-login-por-nome", {
			...(groupId ? { headers: { [DEFAULT_HEADER_GROUP_ID]: groupId } } : {}),
			searchParams: { nome: normalizarNomeUsuario(nome) },
			signal,
		})
		.json<ISpaceAuthLookupResponse[]>();

	return normalizarRespostaLookup(resposta);
}

export async function recuperarLoginPorId(id: number | string, groupId?: string, signal?: AbortSignal) {
	const idNumber = typeof id === "number" ? id : Number(String(id).trim());

	const resposta = await spaceApiAuthenticator
		.get("recupera-login-por-id", {
			...(groupId ? { headers: { [DEFAULT_HEADER_GROUP_ID]: groupId } } : {}),
			searchParams: { id_cad_pessoa: String(idNumber) },
			signal,
		})
		.json<ISpaceAuthLookupResponse | ISpaceAuthLookupResponse[]>();

	return normalizarRespostaLookup(resposta);
}

// ----------------------------------------------- OTP ---------------------------------------------------------------
export async function validarUsuarioOtp(username: string) {
	return spaceApiAuthenticator
		.post("me/procura-usuario-otp", {
			json: { nome_usuario: username.trim() },
		})
		.json<string | null>();
}

// --------------------------------------------- CONTROLE DE MENU ----------------------------------------------------
export async function menuListar() {
	return spaceApiAuthenticator.get("menu/listar").json<ISpaceMenuResponse>();
}

export async function verificaMenuRouteGuard(tag: string) {
	if (!tag) throw new Error("A tag de menu deve ser informada");

	const res = await spaceApiAuthenticator.get("menu/verificar-acesso", {
		searchParams: { tag },
	});

	if (!res.ok) throw new Error(`Erro ao verificar menu: ${res.statusText}`);

	return {
		status: res.status,
		ok: res.ok,
	};
}

// ----------------------------------------------- PERMISSOES ---------------------------------------------------------
export async function verificaPermissao(req: ISpaceVerificaPermissaoRequest) {
	const searchParams = new URLSearchParams();

	searchParams.append("tag_permissao", req.tag_permissao);

	if (req.tipo === "GRUPO") {
		if (!req.id_cad_grupo_usuario)
			throw new Error("O campo id_cad_grupo_usuario é obrigatório para verificar permissão por grupo.");
		searchParams.append("id_cad_grupo_usuario", String(req.id_cad_grupo_usuario));
	}

	const endpoint =
		req.tipo === "GRUPO" ? "permissao/grupo-usuario/verifica-permissao" : "permissao/verifica-permissao";

	const res = await spaceApiAuthenticator.get(endpoint, {
		headers: { [DEFAULT_HEADER_GROUP_ID]: DEFAULT_GROUP_ID },
		searchParams,
		throwHttpErrors: false,
	});

	if (!res.ok) throw new Error(`Erro ao verificar permissão: ${res.status} - ${res.statusText}`);

	return {
		status: res.status,
		ok: res.ok,
	};
}

export const verificaPermissaoComToken = async (tagPermissao: string, token: string) => {
	const baseUrl = obterBaseUrlSpace("authenticator");
	const baseUrlNormalizada = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
	const url = new URL("permissao/verifica-permissao", baseUrlNormalizada);
	url.searchParams.set("tag_permissao", tagPermissao);

	return fetch(url, {
		method: "GET",
		credentials: "omit",
		headers: {
			Authorization: `Bearer ${token}`,
			[DEFAULT_HEADER_GROUP_ID]: DEFAULT_GROUP_ID,
		},
	});
};

export async function verificaRecursosDaTelaCompleto(req: ISpaceVerificaRecursoRequest) {
	const searchParams = new URLSearchParams();

	searchParams.append("tag_recurso", req.tag_recurso);

	if (req.tipo === "GRUPO") {
		if (!req.id_cad_grupo_usuario)
			throw new Error("O campo id_cad_grupo_usuario é obrigatório para verificar recurso por grupo.");
		searchParams.append("id_cad_grupo_usuario", String(req.id_cad_grupo_usuario));
	}

	const endpoint = req.tipo === "GRUPO" ? "permissao/grupo-usuario/por-recurso" : "permissao/por-recurso";

	const res = await spaceApiAuthenticator.get(endpoint, {
		headers: { [DEFAULT_HEADER_GROUP_ID]: DEFAULT_GROUP_ID },
		searchParams,
		throwHttpErrors: false,
	});

	if (!res.ok) throw new Error(`Erro ao verificar permissão: ${res.status} - ${res.statusText}`);

	return await res.json<IVerificaRecursosDaTelaResponse>();
}

// ---------------------------------------------------- GRUPOS USUARIO -----------------------------------------------
export async function listarGruposUsuario(req: IListarGruposUsuarioRequest) {
	const searchParams = new URLSearchParams();

	if (req.listar_estatisticas) searchParams.append("listar_estatisticas", "true");
	if (req.tag_recurso) searchParams.append("tag_recurso", req.tag_recurso);

	return spaceApiAuthenticator
		.get("grupo-usuario/listar", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: DEFAULT_GROUP_ID },
			searchParams,
		})
		.json<IListarGruposUsuarioResponse>();
}

// ------------------------------------- CADASTRO DE RESTRICOES (PERMISSOES POR GRUPO) --------------------------------
export async function obterArvoreRestricoesGrupo(idCadGrupoUsuario: number): Promise<IArvoreRestricoesResponse> {
	if (!isSignedInteger(String(idCadGrupoUsuario)))
		throw new Error("O id_cad_grupo_usuario deve ser informado para obter as restricoes.");

	return spaceApiAuthenticator
		.get("permissao/grupo-usuario/telas", {
			headers: HEADERS_GRUPO_PADRAO,
			searchParams: { id_cad_grupo_usuario: String(idCadGrupoUsuario) },
		})
		.json<IArvoreRestricoesResponse>();
}

export async function listarUsuariosDoGrupo(req: IListarUsuariosGrupoRequest): Promise<IListarUsuariosGrupoResponse> {
	if (!isSignedInteger(String(req.id_cad_grupo_usuario)))
		throw new Error("O id_cad_grupo_usuario deve ser informado para listar os usuarios.");

	const searchParams = new URLSearchParams();
	searchParams.append("id_cad_grupo_usuario", String(req.id_cad_grupo_usuario));
	searchParams.append("page", String(req.page ?? 1));
	searchParams.append("limit", String(req.limit ?? 10));

	if (req.nome__conteudo && req.nome__operador) {
		adicionarParametroConsulta(searchParams, "nome__operador", req.nome__operador);
		adicionarParametroConsulta(searchParams, "nome__conteudo", req.nome__conteudo);
	}

	return spaceApiAuthenticator
		.get("grupo-usuario/usuarios", {
			headers: HEADERS_GRUPO_PADRAO,
			searchParams,
		})
		.json<IListarUsuariosGrupoResponse>();
}

export async function listarAuditoriaGrupo(req: IListarAuditoriaGrupoRequest): Promise<IListarAuditoriaGrupoResponse> {
	if (!isSignedInteger(String(req.id_cad_grupo_usuario)))
		throw new Error("O id_cad_grupo_usuario deve ser informado para listar a auditoria.");

	const searchParams = new URLSearchParams();
	searchParams.append("id_cad_grupo_usuario", String(req.id_cad_grupo_usuario));
	searchParams.append("page", String(req.page ?? 1));
	searchParams.append("limit", String(req.limit ?? 20));

	adicionarParametroConsulta(searchParams, "tipo", req.tipo);
	adicionarParametroConsulta(searchParams, "data_inicio", req.data_inicio);
	adicionarParametroConsulta(searchParams, "data_fim", req.data_fim);

	return spaceApiAuthenticator
		.get("auditoria/permissao/grupo-usuario", {
			headers: HEADERS_GRUPO_PADRAO,
			searchParams,
		})
		.json<IListarAuditoriaGrupoResponse>();
}

export async function listarAuditoriaRecurso(
	req: IListarAuditoriaRecursoRequest,
): Promise<IListarAuditoriaRecursoResponse> {
	if (!req.tag_recurso) throw new Error("O tag_recurso deve ser informado para listar a auditoria do recurso.");

	const searchParams = new URLSearchParams();
	searchParams.append("tag_recurso", req.tag_recurso);
	searchParams.append("page", String(req.page ?? 1));
	searchParams.append("limit", String(req.limit ?? 20));

	adicionarParametroConsulta(searchParams, "tipo", req.tipo);
	adicionarParametroConsulta(searchParams, "data_inicio", req.data_inicio);
	adicionarParametroConsulta(searchParams, "data_fim", req.data_fim);

	return spaceApiAuthenticator
		.get("auditoria/permissao/recurso", {
			headers: HEADERS_GRUPO_PADRAO,
			searchParams,
		})
		.json<IListarAuditoriaRecursoResponse>();
}

// CRUD RESTRICOES
export async function salvarRestricoesGrupo(req: ISalvarRestricoesGrupoRequest): Promise<void> {
	if (!req?.id_cad_grupo_usuario) throw new Error("O id_cad_grupo_usuario e obrigatorio para salvar restricoes.");

	await spaceApiAuthenticator.put("permissao/grupo-usuario/salvar", {
		headers: { ...HEADERS_GRUPO_PADRAO, "Content-Type": "application/json" },
		json: req,
	});
}

export async function criarGrupoUsuario(req: ICriarGrupoUsuarioRequest) {
	if (!req?.descricao?.trim()) throw new Error("A descricao do grupo e obrigatoria.");

	return spaceApiAuthenticator
		.post("grupo-usuario/inserir", {
			headers: { ...HEADERS_GRUPO_PADRAO, "Content-Type": "application/json" },
			json: req,
		})
		.json<ICrudGrupoUsuarioResponse>();
}

export async function editarGrupoUsuario(req: IEditarGrupoUsuarioRequest) {
	if (!req?.id) throw new Error("O id do grupo e obrigatorio para editar.");
	if (!req?.descricao?.trim()) throw new Error("A descricao do grupo e obrigatoria.");

	return spaceApiAuthenticator
		.put("grupo-usuario/atualizar", {
			headers: { ...HEADERS_GRUPO_PADRAO, "Content-Type": "application/json" },
			json: req,
		})
		.json<ICrudGrupoUsuarioResponse>();
}

export async function duplicarGrupoUsuario(req: IClonarGrupoUsuarioRequest) {
	if (!req?.id) throw new Error("O id_origem e obrigatorio para duplicar o grupo.");
	if (!req?.descricao?.trim()) throw new Error("A descricao do grupo e obrigatoria.");

	return spaceApiAuthenticator
		.post("grupo-usuario/clonar", {
			headers: { ...HEADERS_GRUPO_PADRAO, "Content-Type": "application/json" },
			json: req,
		})
		.json<ICrudGrupoUsuarioResponse>();
}

export async function excluirGrupoUsuario(req: IExcluirGrupoUsuarioRequest): Promise<void> {
	if (!req?.id) throw new Error("O id do grupo e obrigatorio para excluir.");

	await spaceApiAuthenticator.delete("grupo-usuario/apagar", {
		headers: { ...HEADERS_GRUPO_PADRAO, "Content-Type": "application/json" },
		json: req,
	});
}
